"use client";
import { Suspense, useEffect, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Network,
  Plus,
  RefreshCw,
  Search
} from "lucide-react";
import { RequireRole } from "../../components/console-shell";
import { Banner, Button, Desk, Fact, Facts, PageHeader, StatusTag } from "../../components/desk";
import { Choice, ReadState, useRead } from "../../components/desk/record-tools";
import {
  flatten,
  type OrgNode,
  type OrgKind,
  type Term,
  type Appointment
} from "../../components/desk/organization-model";
import { OrganizationEditor, type OrgAction } from "../../components/desk/organization-editor";
import type { DictionaryItem } from "../../components/desk/member-model";
import { useSession } from "../../lib/session";
import { formatTime } from "../../lib/api";

export default function OrganizationsPage() {
  return (
    <RequireRole roleCode="ORGANIZATION_ADMIN">
      <Suspense fallback={<Desk>正在读取组织管理…</Desk>}>
        <OrganizationDesk />
      </Suspense>
    </RequireRole>
  );
}
function OrganizationDesk() {
  const params = useSearchParams(),
    { hasRole } = useSession();
  const [selected, setSelected] = useState(params.get("record") ?? ""),
    [word, setWord] = useState(""),
    [folded, setFolded] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState(""),
    [revision, setRevision] = useState(0);
  const [editor, setEditor] = useState<{
    action: OrgAction;
    org: OrgNode;
    term?: Term;
    appointment?: Appointment;
  } | null>(null);
  const tree = useRead<{
    items: OrgNode[];
    meta: { organizationCount: number; memberCount: number };
  }>("/api/v1/orgs/tree");
  const kinds = useRead<{ items: OrgKind[] }>("/api/v1/orgs/kinds");
  const divisions = useRead<{ items: DictionaryItem[] }>(
    "/api/v1/catalog/dictionaries/ADMINISTRATIVE_DIVISION"
  );
  const posts = useRead<{ items: DictionaryItem[] }>(
    "/api/v1/catalog/dictionaries/INNER_PARTY_POST"
  );
  const nodes = flatten(tree.data?.items ?? []),
    org = nodes.find(({ node }) => node.id === selected)?.node;
  const ready = !!kinds.data && !!divisions.data && !!posts.data;
  const optionsError = kinds.error || divisions.error || posts.error;
  useEffect(() => {
    if (!selected && tree.data?.items[0]) setSelected(tree.data.items[0].id);
  }, [selected, tree.data]);
  const start = (action: OrgAction, term?: Term, appointment?: Appointment) => {
    if (org) setEditor({ action, org, term, appointment });
  };
  if (editor)
    return (
      <Desk>
        <OrganizationEditor
          {...editor}
          tree={tree.data?.items ?? []}
          kinds={kinds.data?.items ?? []}
          divisions={divisions.data?.items ?? []}
          posts={posts.data?.items ?? []}
          onCancel={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            setMessage("办理已完成，正在读取最新组织资料。");
            void tree.reload();
            setRevision((r) => r + 1);
          }}
        />
      </Desk>
    );
  const match = (node: OrgNode): boolean =>
    node.name.toLowerCase().includes(word.trim().toLowerCase()) || node.children.some(match);
  const branch = (items: OrgNode[], depth = 0) =>
    items.filter(match).map((node) => (
      <li key={node.id}>
        <div
          className={`organization-node ${selected === node.id ? "is-selected" : ""}`}
          style={{ paddingLeft: `${12 + depth * 16}px` }}
        >
          {node.children.length ? (
            <button
              className="organization-fold"
              aria-label={`${folded.has(node.id) ? "展开" : "收起"}${node.name}`}
              aria-expanded={word.trim() ? true : !folded.has(node.id)}
              onClick={() =>
                setFolded((prev) => {
                  const next = new Set(prev);
                  if (next.has(node.id)) next.delete(node.id);
                  else next.add(node.id);
                  return next;
                })
              }
            >
              {folded.has(node.id) && !word.trim() ? (
                <ChevronRight size={14} />
              ) : (
                <ChevronDown size={14} />
              )}
            </button>
          ) : (
            <span className="organization-spacer" />
          )}
          <button
            className="organization-select"
            aria-current={selected === node.id ? "page" : undefined}
            onClick={() => setSelected(node.id)}
          >
            <span>{node.name}</span>
            <small>
              {node.kind?.displayName ?? "类型待标注"}
              {node.lifecycle === "ARCHIVED" ? " · 已归档" : ""}
            </small>
          </button>
          <span className="organization-count" aria-label={`${node.memberCount}份有效会籍`}>
            {node.memberCount}
          </span>
        </div>
        {node.children.length && (!folded.has(node.id) || word.trim()) ? (
          <ul>{branch(node.children, depth + 1)}</ul>
        ) : null}
      </li>
    ));
  return (
    <Desk wide>
      <PageHeader
        eyebrow="会员与组织 / 组织管理"
        title="组织管理"
        description="沿组织结构查看会籍归属，按届次维护会内任职。"
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void tree.reload();
              setRevision((r) => r + 1);
            }}
          >
            <RefreshCw size={16} />
            刷新
          </Button>
        }
      />
      {message && <Banner tone="success">{message}</Banner>}
      {optionsError && (
        <div className="workspace-error" role="alert">
          办理选项读取失败：{optionsError}
          <Button
            variant="outline"
            onClick={() => {
              void kinds.reload();
              void divisions.reload();
              void posts.reload();
            }}
          >
            重试选项
          </Button>
        </div>
      )}
      <ReadState {...tree} retry={tree.reload} />
      {tree.data && (
        <>
          <div className="record-list-meta">
            <span>
              授权范围内 {tree.data.meta.organizationCount} 个组织 · {tree.data.meta.memberCount}{" "}
              份有效会籍
            </span>
            <span>人数按当前有效会籍实时派生</span>
          </div>
          <div className="organization-workspace">
            <aside className="organization-tree" aria-label="组织结构">
              <div className="organization-tree-heading">
                <Network size={17} />
                <strong>组织结构</strong>
              </div>
              <div className="record-search">
                <Search size={16} />
                <input
                  aria-label="搜索组织名称"
                  placeholder="搜索组织名称"
                  value={word}
                  onChange={(e) => setWord(e.target.value)}
                />
              </div>
              <ul className="organization-branches">{branch(tree.data.items)}</ul>
              {!tree.data.items.some(match) && <p className="record-muted">没有匹配的组织。</p>}
              <p className="organization-tree-note">右侧数字为本组织有效会籍数</p>
            </aside>
            <div className="organization-detail">
              {org ? (
                <>
                  <header className="organization-heading">
                    <div>
                      <div className="record-eyebrow">
                        {org.kind?.displayName ?? "类型待标注"} /{" "}
                        {org.lifecycle === "ACTIVE" ? "在用组织" : "已归档"}
                      </div>
                      <h2>{org.name}</h2>
                      <p>
                        {org.divisionLabel || "区划未登记"}
                        {org.parentId
                          ? ` · 上级：${nodes.find(({ node }) => node.id === org.parentId)?.node.name ?? "未显示"}`
                          : " · 当前授权视图起点"}
                      </p>
                    </div>
                    {org.lifecycle === "ACTIVE" && org.kind?.canHaveChildren !== false && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!ready}
                        onClick={() => start("create")}
                      >
                        <Plus size={15} />
                        新建下级
                      </Button>
                    )}
                  </header>
                  <section className="record-section">
                    <div className="record-section-title">
                      <h3>组织资料</h3>
                      {org.lifecycle === "ACTIVE" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!ready}
                          onClick={() => start("rename")}
                        >
                          更名与区划
                        </Button>
                      )}
                    </div>
                    <Facts>
                      <Fact label="组织简称">{org.shortName || "未登记"}</Fact>
                      <Fact label="有效会籍">{org.memberCount} 份</Fact>
                      <Fact label="直属下级">{org.childCount} 个</Fact>
                      <Fact label="会籍归属">
                        {org.kind
                          ? org.kind.canHoldMembership
                            ? "可承载会籍"
                            : "不承载会籍"
                          : "类型待标注"}
                      </Fact>
                    </Facts>
                    {hasRole("PROFILE_CASE_HANDLER") && (
                      <Link
                        className="record-link"
                        href={`/members?organizationId=${encodeURIComponent(org.id)}`}
                      >
                        查看所属会员档案
                        <ArrowRight size={14} />
                      </Link>
                    )}
                  </section>
                  <TermDesk key={`${org.id}:${revision}`} org={org} ready={ready} start={start} />
                  {org.lifecycle === "ACTIVE" && (
                    <section className="record-section">
                      <h3>组织维护</h3>
                      <div className="workspace-actions">
                        <Button
                          variant="outline"

                          onClick={() => start("move")}
                        >
                          调整上级组织
                        </Button>
                        <Button variant="ghost" onClick={() => start("archive")}>
                          归档组织
                        </Button>
                      </div>
                      <p className="record-muted">
                        调整上级前预览授权影响；归档前核查会籍与未办结事项。
                      </p>
                    </section>
                  )}
                </>
              ) : (
                <div className="workspace-empty">
                  <Network />
                  <strong>选择一个组织</strong>
                  <p>从左侧组织结构查看资料、届次与任职。</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Desk>
  );
}
function TermDesk({
  org,
  ready,
  start
}: {
  org: OrgNode;
  ready: boolean;
  start: (action: OrgAction, term?: Term, appointment?: Appointment) => void;
}) {
  const terms = useRead<{ items: Term[] }>(`/api/v1/orgs/${encodeURIComponent(org.id)}/terms`);
  const [id, setId] = useState("");
  const term =
    terms.data?.items.find((t) => t.id === id) ??
    terms.data?.items.find((t) => t.isCurrent) ??
    terms.data?.items[0];
  const appointments = useRead<{ items: Appointment[] }>(
    term ? `/api/v1/terms/${encodeURIComponent(term.id)}/appointments` : null
  );
  const canManage = org.lifecycle === "ACTIVE";
  return (
    <section className="record-section">
      <div className="record-section-title">
        <h3>届次与任职</h3>
        {canManage && (
          <Button
            variant="outline"
            size="sm"
            disabled={!ready || terms.loading || !!terms.error}
            onClick={() => start("term")}
          >
            <Plus size={14} />
            登记新届次
          </Button>
        )}
      </div>
      <ReadState {...terms} retry={terms.reload} />
      {terms.data?.items.length === 0 && (
        <div className="workspace-empty">
          <strong>尚未登记届次</strong>
          <p>取得届次依据后，登记届次和任职人员。</p>
        </div>
      )}
      {term && (
        <>
          <div className="organization-term-select">
            <Choice
              label="查看届次"
              value={term.id}
              onChange={setId}
              options={terms.data!.items.map((t) => ({
                value: t.id,
                label: `${t.displayName}${t.isCurrent ? " · 现届" : t.closedAt ? " · 已收届" : " · 筹备"}`
              }))}
            />
            <StatusTag tone={term.isCurrent ? "brand" : "neutral"}>
              {term.isCurrent ? "现届" : term.closedAt ? "已收届" : "筹备"}
            </StatusTag>
          </div>
          <p className="record-muted">
            来源：{term.sourceRef} · 开始日期：{term.startOn || "未登记"}
            {term.placeholderRef ? " · 届次事实待核实" : ""}
          </p>
          {term.closedAt && (
            <p className="record-muted">
              {formatTime(term.closedAt)} 收届 · {term.closedReason}
            </p>
          )}
          <ReadState {...appointments} retry={appointments.reload} />
          {appointments.data?.items.length === 0 && (
            <p className="record-muted">本届尚未登记任职人员。</p>
          )}
          {appointments.data?.items.length ? (
            <div className="record-table-scroll">
              <table className="record-table organization-appointments">
                <caption className="sr-only">{term.displayName}任职人员</caption>
                <thead>
                  <tr>
                    <th>任职人员</th>
                    <th>会内职务</th>
                    <th>任职期间</th>
                    <th>状态</th>
                    <th>
                      <span className="sr-only">操作</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.data.items.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <strong>{a.displayName}</strong>
                      </td>
                      <td>{a.postLabel}</td>
                      <td>
                        {a.startOn || "日期未登记"}
                        <small>
                          {a.endOn ? `至 ${a.endOn}` : a.isCurrent ? "至今" : "卸任日期未登记"}
                        </small>
                      </td>
                      <td>
                        <StatusTag tone={a.isCurrent ? "success" : "neutral"}>
                          {a.isCurrent
                            ? "现任"
                            : a.endOn
                              ? "已卸任"
                              : term.closedAt
                                ? "历史任职"
                                : "非现任"}
                        </StatusTag>
                      </td>
                      <td>
                        {canManage && <Button size="sm" variant="outline" onClick={() => start("supersede", term, a)}>更正任职</Button>}
                        {canManage && !term.closedAt && !a.endOn && (
                          <Button size="sm" variant="ghost" onClick={() => start("end", term, a)}>
                            卸任
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {canManage && !term.closedAt && (
            <div className="record-section-title">
              <Button
                variant="outline"
                size="sm"
                disabled={!ready}
                onClick={() => start("appointment", term)}
              >
                登记任职
              </Button>
              <Button variant="ghost" size="sm" onClick={() => start("close", term)}>
                收届
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
